import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { logout } from '../store/authSlice';
import ThemeToggle from './ThemeToggle';

const UserDropdown = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { user } = useAppSelector((state) => state.auth);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const handleLogout = () => {
        dispatch(logout());
        navigate('/');
    };

    const handleAccountClick = () => {
        setIsDropdownOpen(false);
        navigate('/account');
    };

    return (
        <div style={{ position: 'relative' }}>
            <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center rounded-full font-medium transition-all duration-300 hover:scale-105"
                style={{
                    backgroundColor: 'var(--color-gray-100)',
                    padding: '6px 10px',
                    border: '1px solid var(--color-border-light)',
                    gap: '6px',
                    cursor: 'pointer',
                    color: 'var(--color-text-primary)',
                    fontSize: '0.8125rem'
                }}
            >
                <div style={{
                    width: '26px',
                    height: '26px',
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-primary-600)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: '600',
                    fontSize: '0.75rem'
                }}>
                    {user?.email?.charAt(0).toUpperCase()}
                </div>
                <span className="hidden sm:inline" style={{ color: 'var(--color-text-secondary)' }}>{user?.email}</span>
                <svg
                    style={{
                        width: '14px',
                        height: '14px',
                        transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease-in-out',
                        color: 'var(--color-text-secondary)'
                    }}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>

            {/* Dropdown Menu */}
            {isDropdownOpen && (
                <>
                    <div
                        style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: 10
                        }}
                        onClick={() => setIsDropdownOpen(false)}
                    />
                    <div
                        style={{
                            position: 'absolute',
                            top: 'calc(100% + var(--spacing-sm))',
                            right: 0,
                            minWidth: '220px',
                            backgroundColor: 'var(--color-surface)',
                            border: '1px solid var(--color-border-light)',
                            borderRadius: 'var(--radius-lg)',
                            boxShadow: 'var(--shadow-xl)',
                            padding: 'var(--spacing-sm)',
                            zIndex: 50
                        }}
                    >
                        <button
                            onClick={handleAccountClick}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--color-text-primary)',
                                fontSize: '0.9375rem',
                                transition: 'background-color 200ms ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-gray-100)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            Account
                        </button>

                        <div style={{
                            height: '1px',
                            backgroundColor: 'var(--color-border-light)',
                            margin: 'var(--spacing-xs) 0'
                        }} />

                        {/* Theme Toggle in Dropdown */}
                        <div style={{
                            padding: 'var(--spacing-sm) var(--spacing-md)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)',
                                color: 'var(--color-text-primary)',
                                fontSize: '0.9375rem'
                            }}>
                                <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                                Theme
                            </div>
                            <ThemeToggle />
                        </div>

                        <div style={{
                            height: '1px',
                            backgroundColor: 'var(--color-border-light)',
                            margin: 'var(--spacing-xs) 0'
                        }} />

                        <button
                            onClick={handleLogout}
                            style={{
                                width: '100%',
                                textAlign: 'left',
                                padding: 'var(--spacing-sm) var(--spacing-md)',
                                borderRadius: 'var(--radius-md)',
                                backgroundColor: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                color: 'var(--color-danger-600)',
                                fontSize: '0.9375rem',
                                fontWeight: '500',
                                transition: 'background-color 200ms ease-in-out',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-sm)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-danger-50)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                        >
                            <svg style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            Sign out
                        </button>
                    </div>
                </>
            )}
        </div>
    );
};

export default UserDropdown;
